import React from "react";
import LazyImage from "../ui/LazyImage";
import { ArrowLeft } from "lucide-react";

const ResultModal = ({
    isModalOpen, capturedImageBase64, overviewImageUrl, currentHide,
    currentPrice, config, handleSubmitOrder, handleEditPattern,
    isSubmitting, previewData, strapOptions, strapSelections,
    isFormValid, isCondOptionVisible
}) => {
    if (!isModalOpen) return null;

    const infoRowStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 0',
        borderBottom: '1px solid #F0EBE3'
    };

    const labelStyle = {
        fontSize: '0.85rem',
        color: '#8B7D6B',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        flexShrink: 0
    };

    const valueStyle = {
        fontSize: '0.95rem',
        color: '#1A110B',
        fontWeight: '500',
        textAlign: 'right',
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    };

    const dotsStyle = {
        flexGrow: 1,
        borderBottom: '1px dotted #D6CFB9',
        margin: '0 8px',
        opacity: 0.6,
        minWidth: '12px'
    };

    const handleProtectedSubmit = (e) => {
        if (!isFormValid) {
            alert("Please complete all required options before proceeding.");
            return;
        }
        handleSubmitOrder(e);
    };

    const visibleOptions = (strapOptions || []).filter(opt => {
        if (opt.isActive === false || opt.isHidden === true) return false;
        if (isCondOptionVisible) return isCondOptionVisible(opt, strapSelections || {});
        return true;
    });

    return (
        <div
            className="position-fixed w-100 h-100 d-flex justify-content-center align-items-center p-3 p-md-4"
            style={{ top: 0, left: 0, zIndex: 9999, backgroundColor: 'rgba(26, 17, 11, 0.7)', backdropFilter: 'blur(8px)' }}>
            <style>{`
                @keyframes modalFadeIn {
                    from { opacity: 0; transform: translateY(20px) scale(0.98); }
                    to { opacity: 1; transform: translateY(0) scale(1); }
                }
                @keyframes modalSlideUp {
                    from { opacity: 0; transform: translateY(60px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .custom-scrollbar::-webkit-scrollbar { width: 5px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #E2D9C8; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #D4B896; }

                .res-modal-preview-scroll {
                    display: flex;
                    flex-direction: column;
                    flex: 1;
                    overflow-y: auto;
                    max-height: calc(92vh - 80px);
                }
                @media (min-width: 768px) {
                    .res-modal-img-slide {
                        display: flex !important;
                        justify-content: center !important;
                        align-items: center !important;
                    }
                }

                @media (max-width: 767.98px) {
                    .res-modal-container {
                        border-radius: 20px !important;
                        max-height: 96vh !important;
                        animation: modalFadeIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) !important;
                    }
                    .res-modal-main {
                        flex-direction: column !important;
                        height: 100% !important;
                    }

                    /* ── Image strip: horizontal scroll ── */
                    .res-modal-preview {
                        width: 100% !important;
                        height: 320px !important;
                        max-height: 320px !important;
                        min-height: 320px !important;
                        flex-shrink: 0 !important;
                        border-right: none !important;
                        border-bottom: 1px solid #F0EBE3 !important;
                        padding: 0 !important;
                        overflow: hidden !important;
                        position: relative;
                    }
                    .res-modal-preview-scroll {
                        flex-direction: row !important;
                        overflow-x: auto !important;
                        overflow-y: hidden !important;
                        scroll-snap-type: x mandatory !important;
                        -webkit-overflow-scrolling: touch;
                        max-height: 100% !important;
                        height: 100% !important;
                        gap: 0 !important;
                        padding-bottom: 0 !important;
                    }
                    .res-modal-preview-scroll::-webkit-scrollbar { display: none; }
                    .res-modal-img-slide {
                        min-width: 100% !important;
                        width: 100% !important;
                        scroll-snap-align: start;
                        display: flex !important;
                        justify-content: center !important;
                        align-items: center !important;
                        padding: 12px 20px !important;
                        flex-shrink: 0;
                        min-height: 0 !important;
                        margin-bottom: 0 !important;
                    }
                    .res-modal-img-slide img {
                        max-height: 285px !important;
                        max-width: 72% !important;
                        object-fit: contain !important;
                        border-radius: 8px;
                    }
                    .res-modal-scroll-dots { display: flex !important; }
                    .res-modal-dots {
                        max-width: 6px !important;
                        min-width: 4px !important;
                        flex-grow: 0 !important;
                        flex-shrink: 0 !important;
                        margin: 0 3px !important;
                    }

                    /* ── Data section ── */
                    .res-modal-data {
                        width: 100% !important;
                        padding: 1rem 2rem !important;
                        flex: 1 !important;
                        min-height: 0 !important;
                        display: flex !important;
                        flex-direction: column !important;
                        overflow: hidden !important;
                    }
                    .res-modal-data-title {
                        font-size: 0.7rem !important;
                        letter-spacing: 0.5px !important;
                        margin-bottom: 0.2rem !important;
                    }
                    .res-modal-data-scroll {
                        flex: 1 !important;
                        min-height: 0 !important;
                        max-height: none !important;
                        margin-bottom: 0.25rem !important;
                    }
                    .res-modal-info-row { padding: 3px 0 !important; }
                    .res-modal-lbl {
                        font-size: 0.65rem !important;
                        flex-shrink: 0 !important;
                        max-width: 48% !important;
                        overflow: hidden !important;
                        text-overflow: ellipsis !important;
                        white-space: nowrap !important;
                    }
                    .res-modal-val {
                        font-size: 0.72rem !important;
                        white-space: nowrap !important;
                        overflow: hidden !important;
                        text-overflow: ellipsis !important;
                        flex: 1 !important;
                        min-width: 0 !important;
                        text-align: right !important;
                    }

                    /* ── Footer ── */
                    .res-modal-footer { padding-top: 0.4rem !important; }
                    .res-modal-price-row { margin-bottom: 0.4rem !important; }
                    .res-modal-price-row h2 { font-size: 1.2rem !important; }
                    .res-modal-price-label { font-size: 0.58rem !important; }
                    .res-modal-back-btn {
                        padding: 5px 12px !important;
                        font-size: 0.72rem !important;
                    }
                    .res-modal-btn-row { gap: 0.4rem !important; }
                    .res-modal-btn-cart {
                        padding: 0.5rem 0.4rem !important;
                        font-size: 0.76rem !important;
                        letter-spacing: 0.5px !important;
                    }
                    .res-modal-btn-checkout {
                        padding: 0.5rem 0.65rem !important;
                        font-size: 0.72rem !important;
                        white-space: nowrap;
                    }
                }
            `}</style>

            <div
                className="bg-white shadow-lg position-relative d-flex flex-column res-modal-container"
                style={{
                    width: '100%',
                    maxWidth: '1100px',
                    height: 'auto',
                    maxHeight: '92vh',
                    borderRadius: '28px',
                    overflow: 'hidden',
                    animation: 'modalFadeIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
                }}>


                <div className="row g-0 h-100 res-modal-main" style={{ display: 'flex' }}>

                    {/* ── Image Panel ── */}
                    <div
                        className="col-md-5 d-flex flex-column p-4 res-modal-preview"
                        style={{ backgroundColor: '#F8F7F3', borderRight: '1px solid #F0EBE3' }}>

                        <div className="res-modal-preview-scroll custom-scrollbar">
                            {/* Slide 1: Strap mockup */}
                            <div className="res-modal-img-slide" style={{ minHeight: '400px' }}>
                                {capturedImageBase64 ? (
                                    <LazyImage
                                        src={capturedImageBase64}
                                        alt="Strap"
                                        style={{ maxHeight: '480px', maxWidth: '100%', objectFit: 'contain' }} />
                                ) : (
                                    <div className="spinner-border text-muted"></div>
                                )}
                            </div>

                            {/* Slide 2: Hide overview */}
                            <div className="res-modal-img-slide" style={{ paddingTop: '1rem', paddingBottom: '1rem' }}>
                                {overviewImageUrl ? (
                                    <LazyImage
                                        src={overviewImageUrl}
                                        alt="Hide Selection"
                                        style={{ maxHeight: '300px', maxWidth: '100%', objectFit: 'contain', borderRadius: '12px', backgroundColor: '#1a1a1a' }} />
                                ) : (
                                    <div className="text-muted small">No area capture available</div>
                                )}
                            </div>
                        </div>

                        {/* Mobile-only scroll dots */}
                        <div
                            className="res-modal-scroll-dots justify-content-center align-items-center"
                            style={{ display: 'none', gap: '5px', paddingBottom: '6px', paddingTop: '4px' }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#1A110B', display: 'inline-block', opacity: 0.8 }}></span>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#D6CFB9', display: 'inline-block' }}></span>
                        </div>
                    </div>

                    {/* ── Data Panel ── */}
                    <div
                        className="col-md-7 d-flex flex-column p-4 p-lg-5 res-modal-data"
                        style={{ backgroundColor: '#fff' }}>

                        <div className="d-flex align-items-center" style={{ marginBottom: '0.6rem' }}>
                            <h4 className="fw-bold m-0 res-modal-data-title" style={{ letterSpacing: '1px', color: '#1A110B' }}>MY CONFIGURATION</h4>
                        </div>

                        <div className="overflow-y-auto custom-scrollbar res-modal-data-scroll" style={{ maxHeight: 'calc(92vh - 320px)', marginBottom: '0.75rem' }}>
                            <div>
                                <div className="res-modal-info-row" style={infoRowStyle}>
                                    <span className="res-modal-lbl" style={labelStyle}>Leather:</span>
                                    <div className="res-modal-dots" style={dotsStyle}></div>
                                    <span className="res-modal-val" style={valueStyle}>{currentHide?.name}</span>
                                </div>
                                <div className="res-modal-info-row" style={infoRowStyle}>
                                    <span className="res-modal-lbl" style={labelStyle}>Lug Width:</span>
                                    <div className="res-modal-dots" style={dotsStyle}></div>
                                    <span className="res-modal-val" style={valueStyle}>{config?.width}mm</span>
                                </div>
                                <div className="res-modal-info-row" style={infoRowStyle}>
                                    <span className="res-modal-lbl" style={labelStyle}>Buckle Width:</span>
                                    <div className="res-modal-dots" style={dotsStyle}></div>
                                    <span className="res-modal-val" style={valueStyle}>{config?.buckleWidth || config?.width}mm</span>
                                </div>
                                <div className="res-modal-info-row" style={infoRowStyle}>
                                    <span className="res-modal-lbl" style={labelStyle}>Short / Long End:</span>
                                    <div className="res-modal-dots" style={dotsStyle}></div>
                                    <span className="res-modal-val" style={valueStyle}>{config?.shortLength}/{config?.longLength}mm</span>
                                </div>

                                {visibleOptions.map(opt => {
                                    const selectedValue = strapSelections[opt.key];
                                    let displayValue = selectedValue || "Not selected";
                                    let addonText = "";

                                    if (selectedValue === 'on-tone') {
                                        displayValue = "On Tone";
                                    } else if (selectedValue && typeof selectedValue === 'string' && selectedValue.includes('[Addon]')) {
                                        const parts = selectedValue.split('|');
                                        const textPart = parts[0].trim();
                                        const addonPart = parts.length > 1 ? parts[1].trim() : '';
                                        if (textPart && addonPart) {
                                            displayValue = `${textPart} | ${opt.addonLabel || 'No Logo'}`;
                                        } else if (addonPart) {
                                            displayValue = opt.addonLabel || 'No Logo';
                                        } else {
                                            displayValue = textPart || 'None';
                                        }
                                    } else if (opt.choices && selectedValue) {
                                        const choice = opt.choices.find(c => c.value === selectedValue || c.label === selectedValue || c._id === selectedValue);
                                        if (choice) {
                                            displayValue = choice.label;
                                            if (choice.price > 0) addonText = ` (+$${choice.price})`;
                                        }
                                    }

                                    return (
                                        <div key={opt.key} className="res-modal-info-row" style={infoRowStyle}>
                                            <span className="res-modal-lbl" style={labelStyle}>
                                                {opt.label}:{(opt.isRequired || opt.required) && <span className="ms-1" style={{ color: '#dc3545' }}>*</span>}
                                            </span>
                                            <div className="res-modal-dots" style={dotsStyle}></div>
                                            <span className="res-modal-val" style={valueStyle}>
                                                {displayValue}{addonText}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* ── Footer ── */}
                        <div className="border-top res-modal-footer" style={{ borderColor: '#F0EBE3', paddingTop: '0.75rem' }}>
                            <div className="d-flex justify-content-between align-items-center res-modal-price-row" style={{ marginBottom: '0.75rem' }}>
                                <div className="d-flex flex-column">
                                    <span className="text-uppercase fw-bold res-modal-price-label"
                                        style={{ fontSize: '0.7rem', letterSpacing: '1px', color: '#8B7D6B' }}>Estimated Total</span>
                                    <h2 className="m-0 fw-bold" style={{ color: '#22c55e' }}>${currentPrice}</h2>
                                </div>
                                <button
                                    onClick={handleEditPattern}
                                    className="btn fw-bold res-modal-back-btn"
                                    style={{ border: '1.5px solid #1A110B', borderRadius: '50px', fontSize: '0.9rem', color: '#1A110B', padding: '8px 20px', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
                                    <ArrowLeft size={16} className="me-2" />BACK
                                </button>
                            </div>

                            <div className="d-flex res-modal-btn-row" style={{ gap: '0.75rem' }}>
                                <button
                                    onClick={handleProtectedSubmit}
                                    className="btn flex-grow-1 fw-bold shadow-sm res-modal-btn-cart"
                                    style={{
                                        backgroundColor: '#1A110B', color: '#fff',
                                        borderRadius: '50px', fontSize: '1rem',
                                        letterSpacing: '1px', cursor: 'pointer',
                                        padding: '0.75rem 0.5rem',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}
                                    disabled={isSubmitting}>
                                    {isSubmitting ? (
                                        <><span className="spinner-border spinner-border-sm me-2"></span>SUBMITTING...</>
                                    ) : (
                                        <>ADD TO CART</>
                                    )}
                                </button>
                                <button
                                    onClick={handleProtectedSubmit}
                                    className="btn fw-bold shadow-sm res-modal-btn-checkout"
                                    style={{
                                        backgroundColor: '#C5A075', color: '#fff',
                                        borderRadius: '50px', fontSize: '0.9rem',
                                        cursor: 'pointer', padding: '0.75rem 1rem'
                                    }}
                                    disabled={isSubmitting}>
                                    CHECKOUT NOW
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResultModal;
